/**
 * 全局公共参数采集器（Layer 1）
 * 自动采集设备/OS/屏幕/网络等信息
 * 严格对齐 docs/埋点数据格式0317.md 第一章 + 5.1 快速参考（共 36 字段）
 */

import type { CommonParams } from '../types/common'
import type { TrackerConfig } from '../types/config'
import { generateTrackId, generateDeviceUuid, getOrCreateSessionId } from '../utils/uuid'
import {
  parseUserAgent,
  getScreenOrientation,
  getOsTheme,
  getNetworkType,
  getOsLanguage,
  getOsRegion,
  getTimeZone,
  getOsFontSize,
} from '../utils/ua-parser'

/**
 * 获取高精度时间戳（毫秒级，带小数精度）
 * 优先使用 performance.timeOrigin + performance.now()，回退到 Date.now()
 */
function getHighResTimestamp(): number {
  if (typeof performance !== 'undefined' && performance.timeOrigin && performance.now) {
    return Math.round(performance.timeOrigin + performance.now())
  }
  return Date.now()
}

// SDK 版本号（构建时从 package.json 注入）
declare const __SDK_VERSION__: string
const SDK_VERSION = typeof __SDK_VERSION__ !== 'undefined' ? __SDK_VERSION__ : '1.0.0'

/**
 * 采集全局公共参数
 * @param config SDK 配置
 * @returns CommonParams 对象
 */
export function collectCommon(config: TrackerConfig): CommonParams {
  const ua = parseUserAgent()
  const deviceUuid = generateDeviceUuid()

  return {
    // 设备信息
    deviceType: ua.deviceType,
    deviceBrand: config.deviceBrand || ua.deviceBrand,
    deviceModel: config.deviceModel || ua.deviceModel,

    // 操作系统
    os: ua.os,
    osTheme: getOsTheme(),
    osFontSize: getOsFontSize(),
    osLanguage: getOsLanguage(),
    osRegion: getOsRegion(),
    timeZone: getTimeZone(),

    // 应用/容器信息
    appName: config.appName,
    appVersion: config.appVersion,
    appKey: config.appKey,
    platform: 'h5',
    appSdkVersion: SDK_VERSION,
    containerType: config.containerType || ua.containerType || 'h5_browser',
    containerVersion: config.containerVersion || ua.containerVersion,
    webviewVersion: config.webviewVersion || ua.webviewVersion || 'webview版本未知',
    appChannel: config.appChannel || '渠道未知',

    // 网络信息
    networkType: getNetworkType(),
    networkOperator: '运营商未知',
    // ipV4: '',

    // 屏幕信息
    screenSize: typeof screen !== 'undefined'
      ? `${screen.width}*${screen.height}`
      : '屏幕尺寸未知',
    viewportSize: typeof window !== 'undefined'
      ? `${window.innerWidth}*${window.innerHeight}`
      : '视口尺寸未知',
    screenOrientation: getScreenOrientation(),

    // 用户标识
    deviceUuid,
    anonymousId: deviceUuid,
    trackId: generateTrackId(deviceUuid.slice(0, 8)),
    sessionId: getOrCreateSessionId(),
    userId: config.userId || '',

    // 时间（使用 performance.timeOrigin + performance.now() 获取高精度时间戳）
    timestamp: getHighResTimestamp(),
    localTime: new Date().toISOString(),

    // 地理位置（后端解析优先，前端置空）
    // locationProvince: '',
    // locationCity: '',

    // 采样率（动态配置加载后由 tracker 覆盖为实际值）
    samplingRate: 100,

    // 调试与环境
    isDebug: config.debug ?? false,
  }
}

/**
 * 刷新动态字段（每次上报时调用）
 * @param common 现有的 CommonParams
 * @returns 更新后的 CommonParams
 */
export function refreshDynamicFields(common: CommonParams): CommonParams {
  return {
    ...common,
    trackId: generateTrackId(common.deviceUuid.slice(0, 8)),
    timestamp: getHighResTimestamp(),
    localTime: new Date().toISOString(),
    networkType: getNetworkType(),
    osTheme: getOsTheme(),
    viewportSize: typeof window !== 'undefined'
      ? `${window.innerWidth}*${window.innerHeight}`
      : common.viewportSize,
    screenOrientation: getScreenOrientation(),
  }
}
